// Shared form component used by MembershipPlanCreatePage and
// MembershipPlanEditPage. Emits form state upward via `onChange` so the
// parent can pass the same state to the sticky MembershipPreviewCard.
//
// Keeps validation logic and field markup in one place so the two pages
// never drift.

import { type ReactNode } from 'react';
import { Input, RadioGroup, Toggle, TextArea } from '@bbpos/ui';
import type { MembershipTier } from '@/features/memberships';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';

export interface MembershipFormState {
  name: string;
  tier_level: MembershipTier;
  description: string;
  fee_rupees: string;         // form value is a string for clean empty rendering
  duration_days: string;      // same
  discount_percentage: string;
  points_multiplier: string;
  priority_service: boolean;
  auto_renewal: boolean;
  is_active: boolean;
}

export type MembershipFormErrors = Partial<
  Record<
    'name' | 'fee' | 'duration' | 'discount' | 'points_multiplier',
    string
  >
>;

export const DURATION_PRESETS = [
  { days: 30, label: '1 month' },
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
];

export interface MembershipPlanFormProps {
  value: MembershipFormState;
  onChange: (next: MembershipFormState) => void;
  errors: MembershipFormErrors;
  /** True on the edit page — tier_level becomes read-only (see CreatePage comment). */
  tierLocked?: boolean;
  /** Rendered in the footer so the parent controls Cancel/Save labels + loading state. */
  footer: ReactNode;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <fieldset className="mb-7 border-l-4 border-amber-500 pl-5">
      <legend className="sr-only">{title}</legend>
      <div className="mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100">
          {title}
        </h3>
        {hint ? (
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>
        ) : null}
      </div>
      {children}
    </fieldset>
  );
}

export function MembershipPlanForm({
  value,
  onChange,
  errors,
  tierLocked,
  footer,
}: MembershipPlanFormProps) {
  const currency = useTenantCurrency();
  function patch<K extends keyof MembershipFormState>(key: K, next: MembershipFormState[K]) {
    onChange({ ...value, [key]: next });
  }

  // Live duration label — "= 6 months" etc.
  const durationLabel = (() => {
    const n = Number(value.duration_days);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 30) return `= ${n} days`;
    if (n < 365) {
      const months = Math.round(n / 30);
      return `= ${months} month${months === 1 ? '' : 's'}`;
    }
    const years = Math.round((n / 365) * 10) / 10;
    return `= ${years % 1 === 0 ? years.toFixed(0) : years.toFixed(1)} years`;
  })();

  return (
    <div>
      {/* Identity */}
      <Section title="Identity" hint="Name, tier and internal description">
        <Input
          label="Plan Name"
          value={value.name}
          onChange={(v) => patch('name', v.slice(0, 100))}
          error={errors.name}
          placeholder="e.g. Gold Annual"
          required
        />
        <div className="mt-4">
          <RadioGroup
            label={tierLocked ? 'Tier (immutable after creation)' : 'Tier'}
            value={value.tier_level}
            onChange={(v) => patch('tier_level', v as MembershipTier)}
            variant="horizontal"
            disabled={tierLocked}
            options={[
              { value: 'SILVER', label: 'Silver' },
              { value: 'GOLD', label: 'Gold' },
              { value: 'PLATINUM', label: 'Platinum' },
              { value: 'CUSTOM', label: 'Custom' },
            ]}
          />
          {tierLocked ? (
            <p className="mt-2 text-[11px] italic text-neutral-500 dark:text-neutral-400">
              Changing the tier after enrollments exist would break existing members' benefit tier —
              create a new plan instead.
            </p>
          ) : null}
        </div>
        <div className="mt-4">
          <TextArea
            label="Description"
            value={value.description}
            onChange={(v) => patch('description', v)}
            placeholder="What does this plan offer? Shown on the member card (optional)."
            maxLength={500}
            rows={3}
          />
        </div>
      </Section>

      {/* Pricing */}
      <Section title="Pricing" hint="Fee is collected once per subscription period">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Fee"
            type="number"
            min={0}
            step={1 / getSubunitMultiplier(currency)}
            prefix={<span className="text-sm text-neutral-500">{getCurrencySymbol(currency)}</span>}
            value={value.fee_rupees}
            onChange={(v) => patch('fee_rupees', v)}
            error={errors.fee}
            helperText="One-time charge for the full duration"
            required
          />
          <div>
            <Input
              label="Duration"
              type="number"
              min={1}
              max={1095}
              step={1}
              suffix={<span className="text-sm text-neutral-500">days</span>}
              value={value.duration_days}
              onChange={(v) => patch('duration_days', v)}
              error={errors.duration}
              helperText={durationLabel || 'Max 1095 (3 years)'}
              required
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {DURATION_PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => patch('duration_days', String(p.days))}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors tabular-nums ${
                Number(value.duration_days) === p.days
                  ? 'bg-amber-500 text-white ring-1 ring-amber-600 shadow-sm'
                  : 'bg-neutral-100 dark:bg-neutral-700/60 text-neutral-700 dark:text-neutral-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 ring-1 ring-neutral-200/60 dark:ring-neutral-600/60'
              }`}
            >
              {p.label} ({p.days}d)
            </button>
          ))}
        </div>
      </Section>

      {/* Benefits */}
      <Section title="Benefits" hint="What members get — all optional, mix and match">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Discount on every bill"
            type="number"
            min={0}
            max={100}
            step={1}
            suffix={<span className="text-sm text-neutral-500">%</span>}
            value={value.discount_percentage}
            onChange={(v) => patch('discount_percentage', v)}
            error={errors.discount}
            helperText="0 = no discount"
          />
          <Input
            label="Loyalty points multiplier"
            type="number"
            min={1}
            max={10}
            step={0.1}
            suffix={<span className="text-sm text-neutral-500">×</span>}
            value={value.points_multiplier}
            onChange={(v) => patch('points_multiplier', v)}
            error={errors.points_multiplier}
            helperText="1 = normal earn rate"
          />
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-4 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Priority Service
              </p>
              <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                Skip the queue at checkout.
              </p>
            </div>
            <Toggle checked={value.priority_service} onChange={(v) => patch('priority_service', v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-4 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Auto Renewal
              </p>
              <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                Charge the member again at expiry.
              </p>
            </div>
            <Toggle checked={value.auto_renewal} onChange={(v) => patch('auto_renewal', v)} />
          </div>
        </div>
      </Section>

      {/* Publishing */}
      <Section title="Publishing" hint="Control whether new enrollments can start on this plan">
        <div className="flex items-center justify-between rounded-xl bg-gradient-to-br from-success-50 to-teal-50 dark:from-success-900/20 dark:to-teal-900/20 p-4 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {value.is_active ? 'Active — accepting enrollments' : 'Inactive — hidden from members'}
            </p>
            <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
              Existing members retain their benefits until their current term expires.
            </p>
          </div>
          <Toggle checked={value.is_active} onChange={(v) => patch('is_active', v)} />
        </div>
      </Section>

      {footer}
    </div>
  );
}

/**
 * Validate the form state and, if valid, return the payload ready for the
 * backend DTO. Kept out of the component so both Create and Edit pages can
 * share the same rules.
 */
export function validateMembershipForm(
  form: MembershipFormState,
  currency: CurrencyCode = 'INR',
): {
  ok: boolean;
  errors: MembershipFormErrors;
  payload?: {
    name: string;
    tier_level: MembershipTier;
    description?: string;
    fee: number;
    duration_days: number;
    discount_percentage?: number;
    points_multiplier?: number;
    priority_service?: boolean;
    auto_renewal?: boolean;
    is_active?: boolean;
  };
} {
  const errors: MembershipFormErrors = {};

  const name = form.name.trim();
  if (name.length < 2 || name.length > 100) {
    errors.name = 'Name must be 2–100 characters';
  }

  const feeNumber = Number(form.fee_rupees);
  let feePaise = 0;
  if (!Number.isFinite(feeNumber) || feeNumber < 0) {
    errors.fee = `Fee must be ${getCurrencySymbol(currency)}0 or more`;
  } else {
    feePaise = Math.round(feeNumber * getSubunitMultiplier(currency));
  }

  const durationNumber = Number(form.duration_days);
  if (!Number.isFinite(durationNumber) || !Number.isInteger(durationNumber) || durationNumber < 1 || durationNumber > 1095) {
    errors.duration = 'Duration must be between 1 and 1095 days';
  }

  let discountPct: number | undefined;
  if (form.discount_percentage.trim() !== '') {
    const n = Number(form.discount_percentage);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      errors.discount = 'Discount must be 0–100';
    } else {
      discountPct = n;
    }
  }

  let pointsMul: number | undefined;
  if (form.points_multiplier.trim() !== '') {
    const n = Number(form.points_multiplier);
    if (!Number.isFinite(n) || n < 1 || n > 10) {
      errors.points_multiplier = 'Multiplier must be between 1.0 and 10.0';
    } else {
      // not-money: 2dp rounding of a 1.0–10.0 multiplier on a NUMERIC(4,2) column
      pointsMul = Math.round(n * 100) / 100;
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    errors: {},
    payload: {
      name,
      tier_level: form.tier_level,
      description: form.description.trim() || undefined,
      fee: feePaise,
      duration_days: Math.floor(durationNumber),
      discount_percentage: discountPct,
      points_multiplier: pointsMul,
      priority_service: form.priority_service,
      auto_renewal: form.auto_renewal,
      is_active: form.is_active,
    },
  };
}

export const INITIAL_FORM_STATE: MembershipFormState = {
  name: '',
  tier_level: 'GOLD',
  description: '',
  fee_rupees: '999',
  duration_days: '365',
  discount_percentage: '10',
  points_multiplier: '2',
  priority_service: true,
  auto_renewal: true,
  is_active: true,
};
